/**
 * `AppearanceProvider` and `useAppearance()` — the four theme axes in React (DESIGN.md §16.2).
 *
 * The blocking script in `index.html` has already stamped the four attributes before the first
 * paint; this provider takes over from there. It keeps the stored choices, listens to the three
 * media queries that can overrule them, and writes the EFFECTIVE values to `<html>`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  DEFAULT_APPEARANCE,
  MEDIA,
  STORAGE_KEYS,
  parsePreferences,
  resolveAppearance,
  rootAttributes,
  type Accent,
  type AppearancePreferences,
  type Backdrop,
  type GlassLevel,
  type ResolvedMode,
  type SystemSignals,
  type ThemeMode,
} from './appearance-state';

export {
  ACCENTS,
  BACKDROPS,
  GLASS_LEVELS,
  THEME_MODES,
  type Accent,
  type Backdrop,
  type GlassLevel,
  type ThemeMode,
} from './appearance-state';

export interface AppearanceValue {
  mode: ThemeMode;
  resolvedMode: ResolvedMode;
  backdrop: Backdrop;
  /** `flat` while the system asks for more contrast and `followSystem` is on. */
  effectiveBackdrop: Backdrop;
  glass: GlassLevel;
  /** `0` while the system asks for less transparency or more contrast and `followSystem` is on. */
  effectiveGlass: GlassLevel;
  accent: Accent;
  followSystem: boolean;
  /** True while the system is what is keeping glass at 0 — so the page can say why. */
  systemForcesGlassOff: boolean;
  /** True while the system is what is keeping the backdrop flat. */
  systemForcesFlat: boolean;
  setMode: (mode: ThemeMode) => void;
  setBackdrop: (backdrop: Backdrop) => void;
  setGlass: (glass: GlassLevel) => void;
  setAccent: (accent: Accent) => void;
  setFollowSystem: (on: boolean) => void;
  /** System, aurora, 2, blue, follow the system. */
  reset: () => void;
}

const AppearanceContext = createContext<AppearanceValue | null>(null);

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private mode and blocked storage throw on read; the defaults are a perfectly good answer.
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // The choice still applies for this session; it just will not survive a reload.
  }
}

function matches(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

function readSystem(): SystemSignals {
  return {
    prefersDark: matches(MEDIA.dark),
    reducedTransparency: matches(MEDIA.reducedTransparency),
    moreContrast: matches(MEDIA.moreContrast),
  };
}

function readPreferences(): AppearancePreferences {
  return typeof window === 'undefined' ? DEFAULT_APPEARANCE : parsePreferences(readStorage);
}

export function AppearanceProvider({ children }: { children: ReactNode }): ReactNode {
  const [preferences, setPreferences] = useState<AppearancePreferences>(readPreferences);
  const [system, setSystem] = useState<SystemSignals>(readSystem);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const lists = Object.values(MEDIA).map((query) => window.matchMedia(query));
    const onChange = (): void => setSystem(readSystem());
    for (const list of lists) list.addEventListener?.('change', onChange);
    return () => {
      for (const list of lists) list.removeEventListener?.('change', onChange);
    };
  }, []);

  // Another tab changed a preference: follow it, so two windows never disagree about the theme.
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key === null || (Object.values(STORAGE_KEYS) as string[]).includes(event.key)) {
        setPreferences(readPreferences());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const resolved = useMemo(() => resolveAppearance(preferences, system), [preferences, system]);

  useEffect(() => {
    const root = document.documentElement;
    for (const [name, value] of Object.entries(rootAttributes(resolved))) {
      root.setAttribute(name, value);
    }
  }, [resolved]);

  const update = useCallback(
    <K extends keyof AppearancePreferences>(
      field: K,
      key: string,
      value: AppearancePreferences[K],
    ): void => {
      setPreferences((current) => ({ ...current, [field]: value }));
      writeStorage(key, String(value));
    },
    [],
  );

  const setMode = useCallback((mode: ThemeMode) => update('mode', STORAGE_KEYS.theme, mode), [update]);
  const setBackdrop = useCallback(
    (backdrop: Backdrop) => update('backdrop', STORAGE_KEYS.backdrop, backdrop),
    [update],
  );
  const setGlass = useCallback(
    (glass: GlassLevel) => update('glass', STORAGE_KEYS.glass, glass),
    [update],
  );
  const setAccent = useCallback(
    (accent: Accent) => update('accent', STORAGE_KEYS.accent, accent),
    [update],
  );
  const setFollowSystem = useCallback(
    (on: boolean) => update('followSystem', STORAGE_KEYS.followSystem, on),
    [update],
  );
  const reset = useCallback(() => {
    for (const key of Object.values(STORAGE_KEYS)) writeStorage(key, null);
    setPreferences(DEFAULT_APPEARANCE);
  }, []);

  const value = useMemo<AppearanceValue>(
    () => ({
      mode: preferences.mode,
      resolvedMode: resolved.theme,
      backdrop: preferences.backdrop,
      effectiveBackdrop: resolved.backdrop,
      glass: preferences.glass,
      effectiveGlass: resolved.glass,
      accent: preferences.accent,
      followSystem: preferences.followSystem,
      systemForcesGlassOff: resolved.glass !== preferences.glass,
      systemForcesFlat: resolved.backdrop !== preferences.backdrop,
      setMode,
      setBackdrop,
      setGlass,
      setAccent,
      setFollowSystem,
      reset,
    }),
    [preferences, resolved, setMode, setBackdrop, setGlass, setAccent, setFollowSystem, reset],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance(): AppearanceValue {
  const value = useContext(AppearanceContext);
  if (!value) throw new Error('useAppearance must be used inside <AppearanceProvider>.');
  return value;
}
